// Service 16 — รับรองเอกสารนิติบุคคลและหนังสือมอบอำนาจธุรกิจข้ามประเทศ
// อ้างอิง: ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 3 (หุ้นส่วนบริษัท และตัวแทน),
// พระราชบัญญัติบริษัทมหาชนจำกัด พ.ศ. 2535, พระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542,
// กรมพัฒนาธุรกิจการค้า (dbd.go.th), กรมการกงสุล (consular.mfa.go.th),
// สภาทนายความในพระบรมราชูปถัมภ์ — ระเบียบว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney),
// กรมทรัพย์สินทางปัญญา (ipthailand.go.th) และธนาคารแห่งประเทศไทย (bot.or.th)
// Category definitions only — client-safe, no heavy FAQ payload.

export type CorporateDocsFaqCategory =
  | "company-affidavit"
  | "board-resolution"
  | "corporate-poa"
  | "signature-authority"
  | "notarial-certification"
  | "consular-legalization-corporate"
  | "subsidiary-branch-abroad"
  | "banking-kyc-ubo"
  | "trademark-ip-authorization"
  | "contracts-tenders-mna";

export interface CorporateDocsCategoryDef {
  slug: CorporateDocsFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const CORPORATE_DOCS_FAQ_CATEGORIES: CorporateDocsCategoryDef[] = [
  {
    slug: "company-affidavit",
    titleTh: "หนังสือรับรองบริษัทและ Company Affidavit",
    titleEn: "Company Affidavit and DBD Certificates",
    descTh:
      "การคัดหนังสือรับรองนิติบุคคลจากกรมพัฒนาธุรกิจการค้า อายุการใช้งานที่คู่สัญญาต่างประเทศยอมรับ ฉบับภาษาอังกฤษ บัญชีรายชื่อผู้ถือหุ้น (บอจ.5) และหนังสือรับรองตราประทับ",
    descEn:
      "Obtaining the company affidavit from the Department of Business Development, how recent counterparties expect it to be, the English version, the shareholder list (Bor Or Jor 5) and certified company seal records.",
    matchTh: /หนังสือรับรองบริษัท|หนังสือรับรองนิติบุคคล|affidavit|บอจ\.|กรมพัฒนาธุรกิจการค้า|DBD|คัดหนังสือรับรอง|รายชื่อผู้ถือหุ้น/i,
    matchEn: /company affidavit|DBD certificate|certificate of incorporation|shareholder list|Bor Or Jor|company extract/i,
  },
  {
    slug: "board-resolution",
    titleTh: "มติที่ประชุมกรรมการและผู้ถือหุ้น",
    titleEn: "Board and Shareholder Resolutions",
    descTh:
      "การจัดทำและรับรองรายงานการประชุมคณะกรรมการและที่ประชุมผู้ถือหุ้น องค์ประชุม การบอกกล่าวนัดประชุม มติพิเศษ และการนำมติไปใช้กับธนาคารและหน่วยงานต่างประเทศ",
    descEn:
      "Drafting and certifying board minutes and shareholder resolutions, quorum, meeting notice, special resolutions, and using resolutions with banks and foreign authorities.",
    matchTh: /มติที่ประชุม|รายงานการประชุม|คณะกรรมการ|ผู้ถือหุ้น|องค์ประชุม|มติพิเศษ|นัดประชุม/,
    matchEn: /board (minutes|resolution)|shareholder resolution|quorum|meeting notice|special resolution|extraordinary meeting/i,
  },
  {
    slug: "corporate-poa",
    titleTh: "หนังสือมอบอำนาจนิติบุคคลข้ามประเทศ",
    titleEn: "Cross-Border Corporate Power of Attorney",
    descTh:
      "การร่างหนังสือมอบอำนาจในนามบริษัทเพื่อใช้ต่างประเทศ ขอบเขตอำนาจที่ควรระบุ การมอบอำนาจช่วง อากรแสตมป์ อายุของหนังสือมอบอำนาจ และการเพิกถอน",
    descEn:
      "Drafting a corporate power of attorney for use abroad, the scope to state, sub-delegation, stamp duty, validity period and revocation.",
    matchTh: /หนังสือมอบอำนาจ|มอบอำนาจ|ผู้รับมอบอำนาจ|มอบอำนาจช่วง|อากรแสตมป์|เพิกถอน/,
    matchEn: /power of attorney|POA|attorney-in-fact|sub-?delegat|stamp duty|revoke|revocation/i,
  },
  {
    slug: "signature-authority",
    titleTh: "อำนาจลงนาม กรรมการผู้มีอำนาจ และตราประทับ",
    titleEn: "Signing Authority, Authorised Directors and the Company Seal",
    descTh:
      "การตรวจเงื่อนไขอำนาจลงนามในหนังสือรับรอง กรรมการสองคนลงนามร่วม การใช้ตราประทับ ผลของการลงนามเกินอำนาจ และการแก้ไขอำนาจลงนามที่กรมพัฒนาธุรกิจการค้า",
    descEn:
      "Reading the signing condition on the affidavit, two-director joint signature, use of the company seal, the effect of signing beyond authority, and changing the signing condition at the DBD.",
    matchTh: /อำนาจลงนาม|กรรมการผู้มีอำนาจ|ตราประทับ|ลงนามร่วม|เกินอำนาจ|ลายมือชื่อกรรมการ/,
    matchEn: /signing authority|authorised director|authorized director|company seal|joint signature|ultra vires|beyond authority/i,
  },
  {
    slug: "notarial-certification",
    titleTh: "ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารบริษัท",
    titleEn: "Notarial Services Attorney for Corporate Documents",
    descTh:
      "ขอบเขตงานของทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ตามระเบียบสภาทนายความ การรับรองสำเนาถูกต้อง คำสาบาน และการรับรองลายมือชื่อกรรมการ",
    descEn:
      "What a Notarial Services Attorney certified by the Lawyers Council of Thailand can do: certify true copies, administer affidavits and oaths, and witness a director's signature on corporate documents.",
    matchTh: /โนตารี|Notar|ทนายความผู้ทำคำรับรอง|สภาทนายความ|รับรองลายมือชื่อ|สำเนาถูกต้อง|คำสาบาน/i,
    matchEn: /notar|Lawyers Council|certify (a )?signature|true copy|affidavit|oath/i,
  },
  {
    slug: "consular-legalization-corporate",
    titleTh: "นิติกรณ์กรมการกงสุล สถานทูต และ Apostille สำหรับเอกสารบริษัท",
    titleEn: "MFA Legalisation, Embassy Attestation and the Apostille",
    descTh:
      "ลำดับขั้นการรับรองเอกสารบริษัทเพื่อใช้ต่างประเทศ ตั้งแต่คัดเอกสารจาก DBD แปล รับรองคำแปลที่กองสัญชาติและนิติกรณ์ ไปจนถึงการรับรองต่อที่สถานทูตปลายทาง และ Apostille ซึ่งมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570",
    descEn:
      "The chain for corporate documents used abroad: DBD extract, translation, legalisation by the Legalization Division of the Department of Consular Affairs, destination-embassy attestation, and the Apostille effective for Thailand on 28 February 2027.",
    matchTh: /นิติกรณ์|กรมการกงสุล|สถานทูต|Apostille|อพอสติล|รับรองคำแปล|กองสัญชาติ/i,
    matchEn: /legalis|legaliz|consular|apostille|embassy attestation|certified translation/i,
  },
  {
    slug: "subsidiary-branch-abroad",
    titleTh: "ตั้งบริษัทย่อย สาขา และสำนักงานผู้แทนในต่างประเทศ",
    titleEn: "Setting Up Subsidiaries, Branches and Representative Offices",
    descTh:
      "ชุดเอกสารที่บริษัทไทยต้องเตรียมเพื่อจดตั้งบริษัทย่อยหรือสาขาในต่างประเทศ และเอกสารที่บริษัทต่างชาติต้องเตรียมเพื่อตั้งนิติบุคคลในไทยตามพระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542",
    descEn:
      "The document set a Thai company needs to incorporate a subsidiary or branch abroad, and what a foreign parent needs to establish an entity in Thailand under the Foreign Business Act B.E. 2542.",
    matchTh: /บริษัทย่อย|สาขา|สำนักงานผู้แทน|บริษัทแม่|ธุรกิจของคนต่างด้าว|จดตั้งในต่างประเทศ|ต่างประเทศจดตั้งในไทย/,
    matchEn: /subsidiar|branch office|representative office|parent company|Foreign Business Act|incorporat/i,
  },
  {
    slug: "banking-kyc-ubo",
    titleTh: "เปิดบัญชีธุรกิจ KYC และผู้รับประโยชน์ที่แท้จริง (UBO)",
    titleEn: "Corporate Bank Accounts, KYC and Ultimate Beneficial Owners",
    descTh:
      "เอกสารที่ธนาคารเรียกเมื่อเปิดบัญชีนิติบุคคลหรือทำธุรกรรมข้ามประเทศ การประกาศผู้รับประโยชน์ที่แท้จริง แบบ FATCA/CRS และการรับรองเอกสารบริษัทต่างชาติให้ธนาคารไทยยอมรับ",
    descEn:
      "What banks require to open a corporate account or process cross-border transfers, ultimate beneficial owner declarations, FATCA/CRS forms, and certifying a foreign parent's documents so Thai banks accept them.",
    matchTh: /เปิดบัญชี|ธนาคาร|KYC|ผู้รับประโยชน์ที่แท้จริง|UBO|FATCA|CRS|โอนเงินระหว่างประเทศ/i,
    matchEn: /bank account|KYC|beneficial owner|UBO|FATCA|CRS|cross-border (transfer|payment)/i,
  },
  {
    slug: "trademark-ip-authorization",
    titleTh: "มอบอำนาจงานเครื่องหมายการค้าและทรัพย์สินทางปัญญา",
    titleEn: "IP and Trademark Powers of Attorney",
    descTh:
      "หนังสือมอบอำนาจสำหรับยื่นและต่ออายุเครื่องหมายการค้า สิทธิบัตร และลิขสิทธิ์ ทั้งที่กรมทรัพย์สินทางปัญญาและระบบ Madrid Protocol รวมถึงหนังสือยินยอมให้ใช้เครื่องหมาย",
    descEn:
      "Powers of attorney for filing and renewing trademarks, patents and copyright at the Department of Intellectual Property and through the Madrid Protocol, plus letters of consent to use a mark.",
    matchTh: /เครื่องหมายการค้า|สิทธิบัตร|ลิขสิทธิ์|ทรัพย์สินทางปัญญา|Madrid|หนังสือยินยอมให้ใช้เครื่องหมาย/i,
    matchEn: /trademark|patent|copyright|intellectual property|Madrid Protocol|letter of consent/i,
  },
  {
    slug: "contracts-tenders-mna",
    titleTh: "สัญญาธุรกิจ งานประมูล และเอกสาร M&A",
    titleEn: "Commercial Contracts, Tenders and M&A Documents",
    descTh:
      "การรับรองเอกสารประกอบการประมูลงานต่างประเทศ สัญญาสองภาษา หนังสือรับรองสถานะทางการเงิน และชุดเอกสารตรวจสอบสถานะกิจการ (due diligence) ในการซื้อขายหุ้นและกิจการ",
    descEn:
      "Certifying documents for foreign tenders, bilingual contracts, financial standing letters, and the due-diligence pack used in share and business transfers.",
    matchTh: /สัญญา|ประมูล|due diligence|ซื้อขายหุ้น|ควบรวม|M&A|สองภาษา|งบการเงิน/i,
    matchEn: /contract|tender|bid|due diligence|share (purchase|transfer)|merger|bilingual|financial statement/i,
  },
];

export function getCorporateDocsCategoryDef(slug: string): CorporateDocsCategoryDef | undefined {
  return CORPORATE_DOCS_FAQ_CATEGORIES.find((c) => c.slug === slug);
}